import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  CheckCircle,
  Clock,
  Eye,
  MapPin,
  DollarSign,
  Plus,
  Pencil,
  Trophy,
  Users,
  XCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatCurrency, formatDate } from '../utils/formatters';

const STATUS_FILTERS = ['all', 'active', 'completed', 'cancelled'];

function formatTime(value) {
  if (!value) return 'N/A';
  const [hours, minutes] = String(value).split(':').map(Number);
  if (!Number.isFinite(hours)) return value;
  const suffix = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes || 0).padStart(2, '0')} ${suffix}`;
}

function statusClass(status) {
  switch (String(status).toLowerCase()) {
    case 'active': return 'admin-badge-success';
    case 'completed': return 'admin-badge-info';
    case 'cancelled': return 'admin-badge-danger';
    default: return 'admin-badge-neutral';
  }
}

export default function OpenPlays() {
  const navigate = useNavigate();
  const [openPlays, setOpenPlays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('upcoming');
  const [action, setAction] = useState(null);

  const loadOpenPlays = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminService.getOpenPlays();
      setOpenPlays(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load Open Play sessions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOpenPlays();
  }, [loadOpenPlays]);

  const filtered = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return openPlays.filter((openPlay) => {
      if (statusFilter !== 'all' && String(openPlay.status).toLowerCase() !== statusFilter) return false;
      if (dateFilter === 'all') return true;

      const eventDate = new Date(openPlay.date);
      eventDate.setHours(0, 0, 0, 0);
      return dateFilter === 'upcoming' ? eventDate >= today : eventDate < today;
    });
  }, [dateFilter, openPlays, statusFilter]);

  async function updateStatus(openPlay, type) {
    const prompt = type === 'complete'
      ? `Mark “${openPlay.name}” as completed?`
      : `Cancel “${openPlay.name}”? This will remove it from upcoming Open Play.`;

    if (!window.confirm(prompt)) return;

    setAction(`${type}-${openPlay.id}`);
    try {
      if (type === 'complete') {
        await adminService.completeOpenPlay(openPlay.id);
        setOpenPlays((items) => items.map((item) => item.id === openPlay.id ? { ...item, status: 'Completed' } : item));
        toast.success('Open Play marked as completed');
      } else {
        await adminService.cancelOpenPlay(openPlay.id);
        setOpenPlays((items) => items.map((item) => item.id === openPlay.id ? { ...item, status: 'Cancelled' } : item));
        toast.success('Open Play cancelled');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || `Failed to ${type} Open Play`);
    } finally {
      setAction(null);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Open Play & Tournaments</h1>
          <p className="mt-1 text-sm text-[#667085]">Set up and manage sessions across all approved venues</p>
        </div>
        <button type="button" onClick={() => navigate('/openplays/new')} className="admin-btn-primary">
          <Plus size={18} className="mr-2" /> Set Up Open Play
        </button>
      </div>

      <div className="admin-card !p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((status) => (
              <button
                type="button"
                key={status}
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status ? 'admin-tab-active capitalize' : 'admin-tab-inactive capitalize'}
              >
                {status}
              </button>
            ))}
          </div>
          <div className="flex rounded-[14px] bg-[#F1F5F9] p-1">
            {[
              { value: 'upcoming', label: 'Upcoming' },
              { value: 'past', label: 'Past' },
              { value: 'all', label: 'All dates' },
            ].map((filter) => (
              <button
                type="button"
                key={filter.value}
                onClick={() => setDateFilter(filter.value)}
                className={dateFilter === filter.value ? 'admin-tab-active' : 'admin-tab-inactive'}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#264D59] border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="admin-card py-16 text-center">
          <Trophy className="mx-auto h-12 w-12 text-[#94A3B8]" />
          <h2 className="mt-4 font-semibold text-[#1F2937]">No matching Open Play sessions</h2>
          <p className="mt-1 text-sm text-[#667085]">Create a session or change the filters to see more results.</p>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-xs font-medium text-[#667085]">{filtered.length} session{filtered.length === 1 ? '' : 's'}</p>
          {filtered.map((openPlay) => {
            const isActive = String(openPlay.status).toLowerCase() === 'active';
            return (
              <article key={openPlay.id} className="admin-card !p-4">
                <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                  <div className="flex min-w-0 gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#E0F7F4]">
                      <Trophy className="h-5 w-5 text-[#0B7F83]" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="font-semibold text-[#1F2937]">{openPlay.name}</h2>
                        <span className={statusClass(openPlay.status)}>{openPlay.status || 'Active'}</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 text-xs text-[#667085]">
                        <span className="flex items-center gap-1.5"><MapPin size={14} />{openPlay.venueName}</span>
                        <span className="flex items-center gap-1.5"><Calendar size={14} />{formatDate(openPlay.date)}</span>
                        <span className="flex items-center gap-1.5"><Clock size={14} />{formatTime(openPlay.startTime)}–{formatTime(openPlay.endTime)}</span>
                        <span className="flex items-center gap-1.5"><Users size={14} />{openPlay.playerCount} / {openPlay.maxPlayers}</span>
                        <span className="flex items-center gap-1.5"><DollarSign size={14} />{formatCurrency(openPlay.pricePerPlayer)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 sm:justify-end">
                    <button type="button" onClick={() => navigate(`/openplays/${openPlay.id}`)} className="admin-btn-outline !h-10 !rounded-xl !px-4">
                      <Eye size={16} className="mr-2" /> View
                    </button>
                    {isActive && (
                      <>
                        <button
                          type="button"
                          onClick={() => navigate(`/openplays/${openPlay.id}/edit`)}
                          className="admin-btn-outline !h-10 !rounded-xl !px-4"
                        >
                          <Pencil size={16} className="mr-2" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => updateStatus(openPlay, 'complete')}
                          disabled={Boolean(action)}
                          className="admin-btn-secondary !h-10 !rounded-xl !px-4"
                        >
                          <CheckCircle size={16} className="mr-2" />
                          {action === `complete-${openPlay.id}` ? 'Updating…' : 'Complete'}
                        </button>
                        <button
                          type="button"
                          onClick={() => updateStatus(openPlay, 'cancel')}
                          disabled={Boolean(action)}
                          className="admin-btn-danger !h-10 !rounded-xl !px-4"
                        >
                          <XCircle size={16} className="mr-2" />
                          {action === `cancel-${openPlay.id}` ? 'Cancelling…' : 'Cancel'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
